import {publicFetch} from '@fitro/customer-host/api'

export type PartnerDiscoveryResponse = {
    slug: string
    displayName: string
    active: boolean
    clerkOrgId?: string | null
}

export type PartnerDiscoveryStatus =
    | {status: 'active'; slug: string; displayName: string}
    | {status: 'inactive'; slug: string; displayName?: string}
    | {status: 'unknown'; slug: string}
    | {status: 'unavailable'; slug: string}
    | {status: 'unconfigured'}

function resolveApiBaseUrl(): string | null {
    const publicBaseUrl = (process.env.NEXT_PUBLIC_API_BASE_URL || '').trim().replace(/\/$/, '')
    const internalBaseUrl = (process.env.API_INTERNAL_BASE_URL || '').trim().replace(/\/$/, '')
    const baseUrl = typeof window === 'undefined'
        ? (internalBaseUrl || publicBaseUrl)
        : publicBaseUrl
    return baseUrl ? baseUrl : null
}

const CORRELATION_COOKIE = 'fitro_correlation_id'
const PARTNER_DISCOVERY_TIMEOUT_MS = 4000

function isUuid(value: string): boolean {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value)
}

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const prefix = `${encodeURIComponent(name)}=`
    const parts = document.cookie.split(';')
    for (const part of parts) {
        const trimmed = part.trim()
        if (trimmed.startsWith(prefix)) {
            const value = trimmed.slice(prefix.length)
            return value ? decodeURIComponent(value) : null
        }
    }
    return null
}

function ensureCorrelationId(): string | null {
    const existing = readCookie(CORRELATION_COOKIE)
    if (existing && isUuid(existing)) {
        return existing
    }
    if (typeof document === 'undefined') return null

    const created = crypto.randomUUID()
    const secure = window.location.protocol === 'https:'
    const cookie = `${encodeURIComponent(CORRELATION_COOKIE)}=${encodeURIComponent(created)}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
    document.cookie = cookie
    return created
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    try {
        return await publicFetch(url, {
            ...init,
            // Ensure we never hang indefinitely on external dependencies.
            signal: controller.signal,
        })
    } finally {
        clearTimeout(timeoutId)
    }
}

export async function discoverPartnerStatus(slug: string): Promise<PartnerDiscoveryStatus> {
    const normalized = slug?.trim()
    if (!normalized) return {status: 'unconfigured'}
    const baseUrl = resolveApiBaseUrl()
    if (!baseUrl) return {status: 'unconfigured'}

    const url = `${baseUrl}/api/public/partners/${encodeURIComponent(normalized)}`
    try {
        const correlationId = ensureCorrelationId()
        const headers: Record<string, string> = {
            'X-Request-Id': crypto.randomUUID(),
            ...(correlationId ? {'X-Correlation-Id': correlationId} : {}),
        }
        const response = await fetchWithTimeout(
            url,
            {method: 'GET', cache: 'no-store', credentials: 'omit', headers},
            PARTNER_DISCOVERY_TIMEOUT_MS
        )
        if (response.ok) {
            const data = (await response.json().catch(() => null)) as
                | PartnerDiscoveryResponse
                | null
            if (!data) {
                return {status: 'unavailable', slug: normalized}
            }
            const safeSlug = data.slug || normalized
            const displayName = data.displayName || safeSlug
            return data.active
                ? {status: 'active', slug: safeSlug, displayName}
                : {status: 'inactive', slug: safeSlug, displayName}
        }
        if (response.status === 403) {
            return {status: 'inactive', slug: normalized}
        }
        if (response.status === 404) {
            return {status: 'unknown', slug: normalized}
        }
        return {status: 'unavailable', slug: normalized}
    } catch {
        return {status: 'unavailable', slug: normalized}
    }
}
